import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import TmBridgePanel from "@/components/TmBridgePanel";

function toISO(localStr) {
    if (!localStr) return "";
    return new Date(localStr).toISOString();
}

// Mostra una data ISO (UTC) come ora locale italiana (Europe/Rome).
function fmtLocal(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            timeZone: "Europe/Rome",
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

// Formato compatto per gli orari delle buste: "gg/mm HH:MM".
function fmtShort(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            timeZone: "Europe/Rome",
            day: "2-digit", month: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function fmtEur(n) {
    if (!n) return "—";
    if (n >= 1_000_000) return `€${(n / 1_000_000).toFixed(2)}mln`;
    return `€${n.toLocaleString("it-IT")}`;
}

// Link a Transfermarkt: usa il profilo salvato se presente, altrimenti ricerca per nome.
function tmUrl(item) {
    if (item?.tm_link) return item.tm_link;
    const q = encodeURIComponent(item?.player_name || "");
    return `https://www.transfermarkt.it/schnellsuche/ergebnis/schnellsuche?query=${q}`;
}

export default function AdminSvincolati() {
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", season: "25/26", end_date: "", notes: "", is_ugly_auction: false });
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null); // {auction, data}
    const [previewBusy, setPreviewBusy] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await api.get("/auctions");
            setAuctions(data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    /* eslint-disable */
    useEffect(() => {
        refresh();
    }, []);
    /* eslint-enable */

    const create = async () => {
        setErr(null);
        if (!form.name || !form.end_date) {
            setErr("Compila nome e scadenza");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auctions", {
                name: form.name,
                season: form.season,
                end_date: toISO(form.end_date),
                notes: form.notes || null,
                is_ugly_auction: form.is_ugly_auction,
            });
            setForm({ name: "", season: form.season, end_date: "", notes: "", is_ugly_auction: false });
            await refresh();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore");
        } finally {
            setBusy(false);
        }
    };

    const openPreview = async (a) => {
        setPreviewBusy(true);
        setPreview({ auction: a, data: null });
        try {
            const { data } = await api.get(`/auctions/${a.id}/preview`);
            setPreview({ auction: a, data });
        } catch (e) {
            alert(e.response?.data?.detail || "Errore nel caricamento dell'anteprima");
            setPreview(null);
        } finally {
            setPreviewBusy(false);
        }
    };

    const confirmExecute = async () => {
        if (!preview?.auction) return;
        const a = preview.auction;
        setPreviewBusy(true);
        try {
            await api.post(`/auctions/${a.id}/execute`);
            setPreview(null);
            await refresh();
        } catch (e) {
            alert(e.response?.data?.detail || "Errore");
        } finally {
            setPreviewBusy(false);
        }
    };

    const closeAuction = async (a) => {
        try {
            await api.patch(`/auctions/${a.id}`, { status: "closed" });
            await refresh();
        } catch (e) {
            alert(e.response?.data?.detail || "Errore");
        }
    };

    const remove = async (a) => {
        if (!window.confirm(`Eliminare l'asta "${a.name}" e TUTTE le sue offerte?`)) return;
        try {
            await api.delete(`/auctions/${a.id}`);
            await refresh();
        } catch (e) {
            alert(e.response?.data?.detail || "Errore");
        }
    };

    // PRODUZIONE: scarica la LISTA dei giocatori con offerte (pes_id/tm_link) da
    // portare in preview per lo scraping (la preview non conosce le buste di prod).
    const downloadTmBids = async (a) => {
        try {
            const { data } = await api.get(`/auctions/${a.id}/export-tm`);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            const safe = (a.name || "asta").replace(/[^\w-]+/g, "_").slice(0, 40);
            link.href = url;
            link.download = `lista-buste-${safe}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            window.alert(`Esportata la lista di ${data.count} giocatori con offerte.\nCaricala in PREVIEW nello "Strumento TM" per scrapare i prezzi.`);
        } catch (e) {
            alert(e.response?.data?.detail || "Errore durante l'esportazione lista buste");
        }
    };

    // PRODUZIONE: carica il file JSON esportato dalla preview e applica i prezzi TM.
    const uploadTmBids = async (a, file) => {
        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            const values = parsed?.values || parsed;
            if (!Array.isArray(values)) {
                alert("File non valido: manca l'elenco 'values'.");
                return;
            }
            const { data } = await api.post(`/auctions/${a.id}/import-tm`, { values });
            let msg = `Dati TM importati (nome · età · prezzo · foto): ${data.applied}/${data.received}.`;
            if (data.unknown > 0) {
                msg += `\n\n${data.unknown} giocatori non trovati nel DB (pes_id: ${(data.unknown_sample || []).join(", ")}…).`;
            }
            window.alert(msg);
            await refresh();
        } catch (e) {
            alert(e.response?.data?.detail || "Errore durante il caricamento TM (file non valido?)");
        }
    };

    const isPreview = typeof window !== "undefined" && !window.location.hostname.endsWith("emergent.host");
    const [tmToolBusy, setTmToolBusy] = useState(false);
    const [tmProgress, setTmProgress] = useState(null); // {done, total}
    const [tmResumeOnly, setTmResumeOnly] = useState(true); // "riprendi": solo mancanti

    // PREVIEW: carica la lista buste (esportata dalla produzione), avvia lo scrape TM
    // in BACKGROUND (l'avvio risponde subito → niente 520 sul proxy) e poi interroga
    // lo stato finché non finisce, mostrando l'avanzamento. Alla fine scarica il file.
    const scrapeTmFromList = async (file) => {
        setTmToolBusy(true);
        setTmProgress(null);
        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            const arr = parsed?.values || parsed;
            const rows = Array.isArray(arr) ? arr : [];
            const items = rows
                .map((v) => (typeof v === "number" ? { pes_id: v } : {
                    pes_id: v?.pes_id, tm_link: v?.tm_link, tm_id: v?.tm_id, name: v?.name,
                }))
                .filter((x) => x.pes_id != null);
            const pes_ids = items.map((x) => x.pes_id);
            if (!pes_ids.length) {
                alert("File non valido: nessun pes_id trovato.");
                return;
            }

            // 1) Avvio in background (risposta immediata). Modalità gentile sempre attiva;
            //    only_missing = "riprendi" (solo giocatori ancora senza dati).
            const { data: startRes } = await api.post(`/auctions/scrape-tm-list`, {
                pes_ids, items, gentle: true, only_missing: tmResumeOnly,
            });
            if (startRes && startRes.started === false && !startRes.already_running) {
                alert(startRes.detail || "Impossibile avviare lo scrape.");
                return;
            }
            setTmProgress({ done: 0, total: startRes?.total || items.length });

            // 2) Polling dello stato finché non termina
            const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
            let st = null;
            for (;;) {
                await sleep(2500);
                try {
                    const { data } = await api.get(`/auctions/scrape-tm-list/status`);
                    st = data;
                    setTmProgress({ done: st.done || 0, total: st.total || 0 });
                    if (!st.running) break;
                } catch {
                    // errori transitori del proxy durante il polling: continua
                }
            }
            // Se non è arrivato NIENTE di fresco (IP bloccato da TM), niente download:
            // mostra solo il messaggio chiaro e fermati.
            if ((st?.updated || 0) === 0) {
                alert(
                    (st?.error
                        ? st.error
                        : "Transfermarkt non ha restituito dati (probabile blocco IP 403).") +
                    "\n\nNessun file scaricato. Riprova più tardi, senza ripetere troppo spesso.",
                );
                return;
            }
            if (st?.error) {
                alert(`Lo scrape si è interrotto: ${st.error}\n(Scarico comunque i risultati parziali.)`);
            }

            // 3) Genera e scarica il file dai valori raccolti
            const values = st?.values || [];
            const out = {
                generated_at: new Date().toISOString(),
                source: "transfermarkt.it",
                count: values.length,
                values,
            };
            const blob = new Blob([JSON.stringify(out, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "tm-valori-buste.json";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            const failed = (st?.details || []).filter((d) => d.status === "not_found");
            let msg = `TM trovati: ${st?.updated || 0}/${st?.total || items.length}. Scaricato "tm-valori-buste.json".\n` +
                'Caricalo in PRODUZIONE con "⬆ Carica TM buste".';
            if (failed.length > 0) {
                msg += `\n\nNon trovati (${failed.length}): ` +
                    failed.slice(0, 15).map((d) => d.name || d.pes_id).join(", ");
            }
            window.alert(msg);
        } catch (e) {
            alert(e.response?.data?.detail || "Errore durante lo scraping (file non valido?).");
        } finally {
            setTmToolBusy(false);
            setTmProgress(null);
        }
    };

    return (
        <div data-testid="admin-svincolati-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// SVINCOLATI</div>
            <h2 className="op-heading text-3xl mt-2 mb-6">Aste a busta chiusa</h2>

            <TmBridgePanel />
            {isPreview && (
                <div className="op-card p-5 mb-8" style={{ borderLeft: "3px solid rgb(var(--tc-2bb6ff))" }} data-testid="tm-tool-card">
                    <div className="op-label" style={{ color: "rgb(var(--tc-2bb6ff))" }}>// STRUMENTO TM (PREVIEW)</div>
                    <p className="op-mono text-xs text-t8f95a3 mt-2 leading-relaxed">
                        L&apos;asta gira in PRODUZIONE, ma lo scraper Transfermarkt funziona solo qui in preview.
                        <br />1) In <b className="text-tffffff">produzione</b>, su un&apos;asta, clicca <b className="text-tffffff">&quot;⬇ Scarica lista buste&quot;</b>.
                        <br />2) Carica qui sotto quel file: la preview scrapa da Transfermarkt <b className="text-tffffff">nome, età, prezzo e foto</b> e ti scarica <b className="text-tffffff">tm-valori-buste.json</b>.
                        <br />3) In <b className="text-tffffff">produzione</b> carica quel file con <b className="text-tffffff">&quot;⬆ Carica TM buste&quot;</b> → nome, età, prezzo (minimi/stipendi) e foto corretti.
                        <br /><span className="text-t8f95a3">Il giro va &quot;piano&quot; (lotti piccoli + pause) per non farsi bloccare da Transfermarkt.</span>
                    </p>
                    <label className="flex items-center gap-2 mt-3 text-xs text-tc9d2dc cursor-pointer select-none" data-testid="tm-tool-resume-label">
                        <input
                            type="checkbox"
                            checked={tmResumeOnly}
                            disabled={tmToolBusy}
                            onChange={(e) => setTmResumeOnly(e.target.checked)}
                            data-testid="tm-tool-resume-checkbox"
                        />
                        Riprendi: scrapa <b className="text-tffffff">solo i giocatori ancora senza dati</b> (consigliato per spezzare il lavoro in più sessioni)
                    </label>
                    <label
                        className="op-btn op-btn-primary text-xs cursor-pointer inline-block mt-3"
                        data-testid="tm-tool-upload"
                    >
                        {tmToolBusy ? "Scrapo TM…" : "⬆ Carica lista buste e scrapa TM"}
                        <input
                            type="file"
                            accept="application/json,.json"
                            className="hidden"
                            disabled={tmToolBusy}
                            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) scrapeTmFromList(f); }}
                        />
                    </label>
                    {tmProgress && (
                        <div className="mt-3" data-testid="tm-tool-progress">
                            <div className="op-mono text-[11px] text-t8f95a3 mb-1">
                                Scrape TM in background… {tmProgress.done}/{tmProgress.total} giocatori (tieni aperta la pagina per scaricare il file a fine giro)
                            </div>
                            <div className="h-1.5 w-full bg-t22252a overflow-hidden rounded">
                                <div
                                    className="h-full bg-t2bb6ff transition-all"
                                    style={{ width: `${tmProgress.total ? Math.round((tmProgress.done / tmProgress.total) * 100) : 0}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Create */}
            <div className="op-card p-5 mb-8" data-testid="create-auction-form">
                <div className="op-label mb-3">// NUOVA ASTA</div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="op-label block mb-1">Nome</label>
                        <input
                            className="op-input"
                            placeholder="es. Asta svincolati 10 giugno"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="auction-name"
                        />
                    </div>
                    <div>
                        <label className="op-label block mb-1">Stagione</label>
                        <input
                            className="op-input"
                            value={form.season}
                            onChange={(e) => setForm({ ...form, season: e.target.value })}
                            data-testid="auction-season"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="op-label block mb-1">Scadenza buste (apertura)</label>
                        <input
                            type="datetime-local"
                            className="op-input"
                            value={form.end_date}
                            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                            data-testid="auction-end"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="op-label block mb-1">Note (opzionale)</label>
                        <input
                            className="op-input"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            data-testid="auction-notes"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="flex items-start gap-2 cursor-pointer p-3 border border-t22252a hover:border-te8ff00/50 transition-colors" data-testid="auction-ugly-toggle">
                            <input
                                type="checkbox"
                                checked={form.is_ugly_auction}
                                onChange={(e) => setForm({ ...form, is_ugly_auction: e.target.checked })}
                                className="mt-0.5"
                            />
                            <div>
                                <div className="op-mono text-xs text-tffffff">
                                    Asta SETTORE UGLY (under 22)
                                </div>
                                <div className="op-mono text-[10px] text-t8f95a3 mt-0.5">
                                    Mostra solo giocatori con età &lt; 22. Gli acquisti vanno direttamente nel settore Ugly (status &quot;Ugly&quot;). Gli Ugly NON sono facce nuove: nessun tag NFC.
                                </div>
                            </div>
                        </label>
                    </div>
                </div>
                {err && <div className="text-tff4d4d text-sm mt-3">{err}</div>}
                <div className="mt-4 flex justify-end">
                    <button
                        className="op-btn op-btn-primary"
                        onClick={create}
                        disabled={busy}
                        data-testid="create-auction-btn"
                    >
                        {busy ? "Creazione…" : "Apri asta"}
                    </button>
                </div>
            </div>

            {/* List */}
            <div className="op-label mb-3">// ASTE</div>
            {loading ? (
                <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>
            ) : auctions.length === 0 ? (
                <div className="op-card p-5 text-t8f95a3 text-sm">Nessuna asta creata.</div>
            ) : (
                <div className="space-y-4">
                    {auctions.map((a) => (
                        <AuctionAdminRow
                            key={a.id}
                            auction={a}
                            onExecute={() => openPreview(a)}
                            onClose={() => closeAuction(a)}
                            onDelete={() => remove(a)}
                            onDownloadTm={() => downloadTmBids(a)}
                            onUploadTm={(file) => uploadTmBids(a, file)}
                            onRefresh={refresh}
                        />
                    ))}
                </div>
            )}

            {preview && (
                <AuctionPreviewModal
                    auction={preview.auction}
                    data={preview.data}
                    busy={previewBusy}
                    onConfirm={confirmExecute}
                    onClose={() => setPreview(null)}
                />
            )}
        </div>
    );
}

function AuctionAdminRow({ auction, onExecute, onClose, onDelete, onDownloadTm, onUploadTm, onRefresh }) {
    const [dlBusy, setDlBusy] = useState(false);
    const [upBusy, setUpBusy] = useState(false);
    // Lo scraper TM funziona solo in PREVIEW (in produzione l'IP è bloccato).
    const isPreview = typeof window !== "undefined" && !window.location.hostname.endsWith("emergent.host");
    const handleDl = async () => {
        setDlBusy(true);
        try { await onDownloadTm(); } finally { setDlBusy(false); }
    };
    const handleUp = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUpBusy(true);
        try { await onUploadTm(file); } finally { setUpBusy(false); }
    };
    return (
        <div
            className="op-card p-4"
            style={{ borderLeft: `3px solid ${{
                open: "rgb(var(--tc-2bb6ff))", closed: "rgb(var(--tc-8f95a3))",
                tie_resolution: "rgb(var(--tc-ff7a1a))", executed: "rgb(var(--tc-e8ff00))",
            }[auction.status] || "rgb(var(--tc-8f95a3))"}` }}
            data-testid={`admin-auction-${auction.id}`}
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="op-heading text-xl">{auction.name}</div>
                        {auction.is_ugly_auction && (
                            <span className="op-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border border-te8ff00 text-te8ff00" data-testid={`ugly-badge-${auction.id}`}>
                                Settore Ugly
                            </span>
                        )}
                    </div>
                    <div className="op-mono text-xs text-t8f95a3 mt-1">
                        {auction.season} · scadenza {fmtLocal(auction.end_date)}
                    </div>
                    <div className="op-label text-[10px] mt-1" style={{ color: {
                        open: "rgb(var(--tc-2bb6ff))", closed: "rgb(var(--tc-8f95a3))",
                        tie_resolution: "rgb(var(--tc-ff7a1a))", executed: "rgb(var(--tc-e8ff00))",
                    }[auction.status] }}>
                        {auction.status === "open" && "APERTA"}
                        {auction.status === "closed" && "CHIUSA (da eseguire)"}
                        {auction.status === "tie_resolution" && "RISOLVI PAREGGI"}
                        {auction.status === "executed" && "ESEGUITA"}
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button
                        className="op-btn text-xs"
                        style={{ borderColor: "rgb(var(--tc-2bb6ff))", color: "rgb(var(--tc-2bb6ff))" }}
                        onClick={handleDl}
                        disabled={dlBusy}
                        title="Scarica la lista dei giocatori con offerte (pes_id + tm_link) da portare in preview per lo scraping TM. Funziona anche dopo l'assegnazione."
                        data-testid={`download-tm-${auction.id}`}
                    >
                        {dlBusy ? "Esporto…" : "⬇ Scarica lista buste"}
                    </button>
                    {!isPreview && (
                        <label
                            className="op-btn text-xs cursor-pointer"
                            style={{ borderColor: "rgb(var(--tc-7ce38b))", color: "rgb(var(--tc-7ce38b))" }}
                            title="Carica il file tm-valori-buste.json generato in preview. Aggiorna anche i giocatori GIÀ assegnati (nome/età/valore/foto)."
                            data-testid={`upload-tm-${auction.id}`}
                        >
                            {upBusy ? "Carico TM…" : "⬆ Carica TM buste"}
                            <input
                                type="file"
                                accept="application/json,.json"
                                onChange={handleUp}
                                disabled={upBusy}
                                className="hidden"
                                data-testid={`upload-tm-input-${auction.id}`}
                            />
                        </label>
                    )}
                    {auction.status === "open" && (
                        <>
                            <button className="op-btn" onClick={onClose} data-testid={`close-${auction.id}`}>
                                Chiudi buste
                            </button>
                            <button className="op-btn op-btn-primary" onClick={onExecute} data-testid={`execute-${auction.id}`}>
                                Apri buste ora
                            </button>
                        </>
                    )}
                    {auction.status === "closed" && (
                        <button className="op-btn op-btn-primary" onClick={onExecute} data-testid={`execute-${auction.id}`}>
                            Apri buste
                        </button>
                    )}
                    <button className="op-btn" onClick={onDelete} data-testid={`delete-${auction.id}`}>
                        Elimina
                    </button>
                </div>
            </div>

            {auction.status === "tie_resolution" && (
                <TieResolver auction={auction} onRefresh={onRefresh} />
            )}

            {(auction.status === "executed" || auction.status === "tie_resolution") && (
                <ResultsView results={auction.results || []} auctionId={auction.id} onRefresh={onRefresh} />
            )}
        </div>
    );
}

function TieResolver({ auction, onRefresh }) {
    const ties = (auction.results || []).filter((r) => r.status === "tie");
    const [picks, setPicks] = useState({});
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    if (ties.length === 0) return null;

    const submit = async () => {
        setErr(null);
        if (Object.keys(picks).length === 0) {
            setErr("Scegli almeno un vincitore");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/auctions/${auction.id}/resolve-ties`, { winners: picks });
            setPicks({});
            await onRefresh();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-4 pt-4 border-t border-t22252a" data-testid={`tie-resolver-${auction.id}`}>
            <div className="op-label mb-3" style={{ color: "rgb(var(--tc-ff7a1a))" }}>
                // RISOLVI PAREGGI ({ties.length})
            </div>
            <div className="space-y-3">
                {ties.map((r) => (
                    <div key={r.player_pes_id} className="op-card p-3 bg-t0c0e11" data-testid={`tie-row-${r.player_pes_id}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <div className="text-tffffff font-semibold">{r.player_name}</div>
                                <div className="op-mono text-xs text-tff7a1a">{fmtEur(r.amount)}</div>
                            </div>
                        </div>
                        <div className="op-mono text-[10px] text-t8f95a3 mb-2">
                            Ordine cronologico delle buste (la 1ª è di chi ha offerto per primo). La scelta resta manuale.
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {(r.tied_team_ids || []).map((tid, i) => (
                                <div key={tid} className="flex flex-col items-start gap-1">
                                    <button
                                        className="op-btn op-mono text-xs"
                                        style={picks[r.player_pes_id] === tid ? {
                                            background: "rgb(var(--tc-2bb6ff))", color: "rgb(var(--tc-000000))", borderColor: "rgb(var(--tc-2bb6ff))"
                                        } : {}}
                                        onClick={() => setPicks({ ...picks, [r.player_pes_id]: tid })}
                                        data-testid={`pick-${r.player_pes_id}-${tid}`}
                                    >
                                        {i === 0 && <span className="text-te8ff00 mr-1">1ª</span>}
                                        {r.tied_team_names?.[i] || tid}
                                    </button>
                                    <span className="op-mono text-[9px] text-t5c6370" data-testid={`tie-time-${r.player_pes_id}-${tid}`}>
                                        {fmtShort(r.tied_team_times?.[i])}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {err && <div className="text-tff4d4d text-sm mt-3">{err}</div>}
            <div className="mt-3 flex justify-end">
                <button
                    className="op-btn op-btn-primary"
                    onClick={submit}
                    disabled={busy}
                    data-testid="resolve-ties-btn"
                >
                    {busy ? "Salvataggio…" : "Conferma vincitori"}
                </button>
            </div>
        </div>
    );
}

function ResultsView({ results, auctionId, onRefresh }) {
    const won = results.filter((r) => r.status === "won");
    const [voiding, setVoiding] = useState(null);

    const voidPurchase = async (r) => {
        if (!window.confirm(`Annullare l'acquisto di ${r.player_name} (${r.winner_team_name})?\n\nIl giocatore torna svincolato, il budget della squadra viene rimborsato e il movimento viene stornato dalla Cassa OP (se ancora presente).`)) return;
        setVoiding(r.player_pes_id);
        try {
            const { data } = await api.post(`/auctions/${auctionId}/void-purchase`, { player_pes_id: r.player_pes_id });
            const opMsg = data.op_reversed ? "Cassa OP stornata." : "Movimento OP non presente (già rimosso): non stornato di nuovo.";
            alert(`✓ Acquisto annullato.\nRimborsato alla squadra: €${(data.refunded / 1e6).toFixed(2)}mln\n${opMsg}`);
            await onRefresh?.();
        } catch (e) {
            alert(e.response?.data?.detail || "Errore nell'annullamento");
        } finally {
            setVoiding(null);
        }
    };

    return (
        <div className="mt-4 pt-4 border-t border-t22252a">
            <div className="op-label mb-2">// ESITO ({won.length} assegnati su {results.length})</div>
            {results.length === 0 ? (
                <div className="op-mono text-xs text-t8f95a3">Nessuna offerta ricevuta.</div>
            ) : (
                <div className="op-mono text-xs space-y-1">
                    {results.map((r) => (
                        <div key={r.player_pes_id} data-testid={`result-row-${r.player_pes_id}`}>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-t5c6370">{r.status === "won" ? "✓" : r.status === "voided" ? "✗" : "⏳"}</span>
                                <span className={`flex-1 min-w-[120px] ${r.status === "voided" ? "text-t5c6370 line-through" : "text-tffffff"}`}>{r.player_name}</span>
                                {r.winner_team_name && (
                                    <span className="text-t2bb6ff">{r.winner_team_name}</span>
                                )}
                                <span className="text-te8ff00">{fmtEur(r.amount)}</span>
                                {r.status === "won" && (
                                    <button
                                        onClick={() => voidPurchase(r)}
                                        disabled={voiding === r.player_pes_id}
                                        className="op-mono text-[10px] px-2 py-0.5 border border-tff3b30/50 text-tff3b30 hover:bg-tff3b30/10 disabled:opacity-40"
                                        data-testid={`void-purchase-${r.player_pes_id}`}
                                    >
                                        {voiding === r.player_pes_id ? "…" : "Annulla acquisto"}
                                    </button>
                                )}
                                {r.status === "voided" && (
                                    <span className="op-mono text-[10px] text-tff3b30">ANNULLATO</span>
                                )}
                            </div>
                            {(r.all_bids || []).length > 0 && (
                                <details className="ml-5 mt-1" data-testid={`result-bids-${r.player_pes_id}`}>
                                    <summary className="cursor-pointer text-[10px] text-t5c6370 hover:text-t8f95a3">
                                        {r.all_bids.length} buste — mostra ordine e orari
                                    </summary>
                                    <div className="mt-1 space-y-0.5 pl-2 border-l border-t181b20">
                                        {[...r.all_bids]
                                            .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
                                            .map((b, i) => (
                                                <div key={i} className="flex items-center justify-between text-[10px]">
                                                    <span className="text-tc9ccd3"><span className="text-t5c6370 mr-1">{i + 1}.</span>{b.team_name}</span>
                                                    <span className="text-t8f95a3"><span className="text-t5c6370 mr-2">{fmtShort(b.created_at)}</span>{fmtEur(b.amount)}</span>
                                                </div>
                                            ))}
                                    </div>
                                </details>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function AuctionPreviewModal({ auction, data, busy, onConfirm, onClose }) {
    const loading = !data;
    const [byTime, setByTime] = useState(false);
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={onClose}
            data-testid="auction-preview-modal"
        >
            <div
                className="op-card w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-t22252a bg-t0e1014 shrink-0">
                    <div className="op-label text-t2bb6ff">⚖️ ANTEPRIMA BUSTE — {auction.name}</div>
                    {loading ? (
                        <div className="op-mono text-[10px] text-t8f95a3 mt-1 animate-pulse">// CARICAMENTO…</div>
                    ) : (
                        <div className="op-mono text-[10px] text-t8f95a3 mt-1">
                            {data.total_players} giocatori · {data.total_bids} offerte ·{" "}
                            <span className={data.ties > 0 ? "text-tff7a1a" : "text-t8f95a3"}>
                                {data.ties} pareggi
                            </span>
                            {(data.below_tm || 0) > 0 && (
                                <>
                                    {" "}·{" "}
                                    <span className="text-tff3b30">{data.below_tm} sotto TM (non assegnati)</span>
                                </>
                            )}
                            {" "}· Nessuna assegnazione finché non confermi.
                        </div>
                    )}
                    {!loading && data.items.length > 0 && (
                        <div className="mt-2 flex items-center gap-2" data-testid="preview-sort-toggle">
                            <span className="op-mono text-[10px] text-t8f95a3">Ordina buste:</span>
                            <button
                                onClick={() => setByTime(false)}
                                className={`op-mono text-[10px] px-2 py-0.5 border ${!byTime ? "border-t2bb6ff text-t2bb6ff" : "border-t22252a text-t8f95a3"}`}
                                data-testid="preview-sort-price"
                            >
                                per prezzo
                            </button>
                            <button
                                onClick={() => setByTime(true)}
                                className={`op-mono text-[10px] px-2 py-0.5 border ${byTime ? "border-t2bb6ff text-t2bb6ff" : "border-t22252a text-t8f95a3"}`}
                                data-testid="preview-sort-time"
                            >
                                cronologico (1ª busta → ultima)
                            </button>
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {loading ? (
                        <div className="op-label text-t8f95a3 animate-pulse py-8 text-center">// CARICAMENTO ANTEPRIMA…</div>
                    ) : data.items.length === 0 ? (
                        <div className="op-card p-5 text-t8f95a3 text-sm text-center">Nessuna offerta ricevuta su questa asta.</div>
                    ) : (
                        data.items.map((it) => (
                            <div
                                key={it.player_pes_id}
                                className="op-card p-3"
                                style={{ borderLeft: `3px solid ${it.below_tm ? "rgb(var(--tc-ff3b30))" : it.is_tie ? "rgb(var(--tc-ff7a1a))" : "rgb(var(--tc-2bb6ff))"}` }}
                                data-testid={`preview-player-${it.player_pes_id}`}
                            >
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-tffffff font-bold text-sm truncate">
                                            {it.player_name}
                                            <span className="op-mono text-[10px] text-t8f95a3 ml-2">
                                                {it.player_position || "—"} · VG {it.player_vg ?? "—"}
                                            </span>
                                        </div>
                                        <div className="op-mono text-[10px] mt-0.5">
                                            {it.market_value > 0 ? (
                                                <span className="text-t8f95a3">Prezzo TM: <span className="text-tffffff">{fmtEur(it.market_value)}</span></span>
                                            ) : (
                                                <span className="text-t8f95a3">Prezzo TM: — (non nel DB)</span>
                                            )}
                                            <a
                                                href={tmUrl(it)}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                className="text-t2bb6ff hover:underline ml-2"
                                                data-testid={`preview-tmlink-${it.player_pes_id}`}
                                            >
                                                verifica su TM ↗
                                            </a>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        {it.below_tm ? (
                                            <div>
                                                <span className="op-mono text-[10px] uppercase px-2 py-1 border border-tff3b30 text-tff3b30">
                                                    NON ASSEGNATO
                                                </span>
                                                <div className="op-mono text-[9px] text-tff3b30 mt-1">
                                                    offerte sotto TM · resta svincolato
                                                </div>
                                            </div>
                                        ) : it.is_tie ? (
                                            <span className="op-mono text-[10px] uppercase px-2 py-1 border border-tff7a1a text-tff7a1a">
                                                PAREGGIO {fmtEur(it.top_amount)}
                                            </span>
                                        ) : (
                                            <div>
                                                <div className="op-label text-[9px] text-t8f95a3">Vincitore</div>
                                                <div className="op-mono text-sm text-te8ff00 font-bold">{it.winner_team_name}</div>
                                                <div className="op-mono text-xs text-tffffff">{fmtEur(it.top_amount)}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* tutte le buste */}
                                <div className="mt-2 pt-2 border-t border-t181b20 space-y-1">
                                    {(byTime
                                        ? [...it.bids].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
                                        : it.bids
                                    ).map((b, i) => (
                                        <div key={i} className="flex items-center justify-between op-mono text-[11px]">
                                            <span className={b.below_tm ? "text-tff3b30" : "text-tc9ccd3"}>
                                                {byTime && <span className="text-t5c6370 mr-1">{i + 1}.</span>}
                                                {b.team_name} {b.below_tm && "⚠ sotto TM"}
                                            </span>
                                            <span className="text-t8f95a3">
                                                <span className="text-t5c6370 mr-2">{fmtShort(b.created_at)}</span>
                                                {fmtEur(b.amount)} · {b.contract_years ?? "—"} anni
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                {!loading && (data.tm_stale?.length || 0) > 0 && (
                    <div className="px-5 py-3 border-t border-tff3b30/40 bg-t0e1014 op-mono text-[11px] text-tff3b30" data-testid="preview-tm-stale">
                        ⛔ Prezzi TM non aggiornati dopo la chiusura ({data.tm_stale.length}): {data.tm_stale.map((s) => s.name).join(", ")}.
                        <span className="text-t8f95a3"> Aggiornali dalla preview (Ponte TM) o inserisci il valore a mano nella pagina del giocatore: finché non sono aggiornati non puoi assegnare.</span>
                    </div>
                )}
                <div className="px-5 py-4 border-t border-t22252a bg-t0e1014 shrink-0 flex items-center justify-between gap-3 flex-wrap">
                    <div className="op-mono text-[10px] text-t8f95a3">
                        {!loading && (data.below_tm || 0) > 0
                            ? `⛔ ${data.below_tm} giocatori sotto TM NON verranno assegnati (restano svincolati).`
                            : !loading && data.ties > 0
                            ? `⚠ ${data.ties} pareggi: dopo la conferma li risolvi nella schermata RISOLVI PAREGGI.`
                            : "Confermando, i giocatori vengono assegnati al miglior offerente (se ≥ TM)."}
                    </div>
                    <div className="flex gap-2">
                        <button className="op-btn text-xs" onClick={onClose} disabled={busy} data-testid="preview-cancel">
                            Annulla
                        </button>
                        <button
                            className="op-btn op-btn-primary text-xs disabled:opacity-50"
                            onClick={onConfirm}
                            disabled={busy || loading || (data?.tm_stale?.length || 0) > 0}
                            data-testid="preview-confirm-assign"
                        >
                            {busy ? "Assegnazione…" : "✓ Conferma e assegna"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
